import React, { ReactNode } from 'react'
import cn from 'classnames'
import { Medal, Share2, UserPlus } from 'lucide-react'
import { captureReportShareClick } from 'components/shared/captureReportShareClick'
import ShareToEarnLabel from 'components/shared/ShareToEarnLabel'
import styles from './upvalue-leaderboard-widget.module.scss'
import InitialsAvatar from 'components/users/InitialsAvatar'
import { avatarInitials } from 'components/users/avatarInitials'

export type LeaderboardCategoryKey =
    | 'received'
    | 'given'
    | 'boosted'
    | 'minters'
    | 'ai_fees'
    | 'hosts'
    | 'sharers'

export type LeaderboardCategoryDef = {
    key: LeaderboardCategoryKey
    label: string
}

export const LEADERBOARD_CATEGORIES: LeaderboardCategoryDef[] = [
    { key: 'received', label: 'Creator' },
    { key: 'given', label: 'Patron' },
    { key: 'boosted', label: 'Booster' },
    { key: 'minters', label: 'Artist' },
    { key: 'ai_fees', label: 'Cyborg' },
    { key: 'hosts', label: 'Host' },
    { key: 'sharers', label: 'Sharer' },
]

export type LeaderboardPanelItem = {
    category: LeaderboardCategoryDef
    user: {
        id: string
        name: string
        avatar_url?: string | null
    }
    valueLabel: ReactNode
}

interface LeaderboardPanelProps {
    className?: string
    embedded?: boolean
    error?: boolean
    headerLabel: ReactNode
    isLoading?: boolean
    items: LeaderboardPanelItem[]
    onOpenLeaderboardModal: () => void
    onSelectUser: (userId: string) => void
    onShare?: () => void
    onJoin?: () => void
}

export default function LeaderboardPanel({
    className,
    embedded = false,
    error = false,
    headerLabel,
    isLoading = false,
    items,
    onOpenLeaderboardModal,
    onSelectUser,
    onShare,
    onJoin,
}: LeaderboardPanelProps) {
    return (
        <div className={cn(styles.container, embedded && styles.embeddedContainer, className)}>
            <div className={styles.list}>
                <div className={styles.sectionHeaderContainer}>
                    <div className={styles.sectionHeaderLabel}>{headerLabel}</div>
                </div>

                <div className={styles.listBody}>
                    {isLoading && <div className={styles.loadingRow}>Loading…</div>}
                    {!isLoading && error && (
                        <div className={styles.errorRow}>Failed to load leaderboard</div>
                    )}
                    {!isLoading &&
                        !error &&
                        items.map(item => (
                            <div
                                key={item.category.key}
                                className={styles.row}
                                onClick={() => onSelectUser(item.user.id)}
                            >
                                <div className={styles.rank}>
                                    <Medal size={12} className={styles.medalIcon} />
                                </div>
                                <div className={styles.categoryLabel}>{item.category.label}</div>
                                {item.user.avatar_url ? (
                                    <img
                                        className={styles.avatar}
                                        src={item.user.avatar_url}
                                        alt={item.user.name}
                                    />
                                ) : avatarInitials(item.user.name) ? (
                                    <InitialsAvatar className={styles.avatar} user={item.user} />
                                ) : (
                                    <div className={styles.avatar} />
                                )}
                                <div className={styles.name}>{item.user.name}</div>
                                <div className={styles.value}>{item.valueLabel}</div>
                            </div>
                        ))}
                    {!isLoading && !error && items.length === 0 && (
                        <div className={styles.emptyRow}>No data yet</div>
                    )}
                </div>

                {onShare ? (
                    <button
                        type="button"
                        className={styles.shareWide}
                        onClick={event => captureReportShareClick(event, onShare)}
                        data-testid="leaderboard-embed-share-button"
                    >
                        <Share2 size={15} aria-hidden />
                        <ShareToEarnLabel fallback="Share this report" />
                    </button>
                ) : onJoin ? (
                    <button
                        type="button"
                        className={styles.shareWide}
                        onClick={onJoin}
                        data-testid="leaderboard-embed-join-button"
                    >
                        <UserPlus size={15} aria-hidden />
                        <span>Join Treechat</span>
                    </button>
                ) : null}

                <div className={styles.listFooter} onClick={onOpenLeaderboardModal}>
                    <span>View Leaderboards</span>
                    <i className="fa fa-chevron-right" />
                </div>
            </div>
        </div>
    )
}
